"use client";
import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  GraduationCap,
  Building2,
  Users,
  DollarSign,
  Clock,
  MapPin,
  Image as ImageIcon,
  Award,
} from "lucide-react";
import { useCategories } from "../context/CategoryContext";
import { useDepartments } from "../context/DepartmentContext";
import { useLecturers } from "../context/LecturerContext";
import { useProgramCategories } from "../context/ProgramCategoryContext";
import { useProgrammes } from "../context/ProgrammeContext";
import { IMG_URL } from "../config/config";

const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
    purple: "bg-purple-50 text-purple-700 ring-purple-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

const SectionDivider = ({ label, right }) => (
  <div className="flex items-center gap-3 my-8">
    {label ? (
      <span className="inline-flex items-center rounded-full bg-white px-4 py-2 text-xs font-extrabold text-gray-800 ring-1 ring-gray-200 shadow-sm">
        {label}
      </span>
    ) : null}
    <div className="h-px flex-1 bg-gradient-to-r from-gray-200 via-gray-200 to-transparent" />
    {right ? <div className="flex items-center gap-2">{right}</div> : null}
  </div>
);

export default function ProgramsPage() {
  const { categories, fetchCategories } = useCategories();
  const { departments, fetchDepartments } = useDepartments();
  const { lecturers, fetchLecturers } = useLecturers();
  const { programCategories, fetchProgramCategories } = useProgramCategories();
  const { programmes, fetchProgrammes, addProgramme, updateProgramme, deleteProgramme } = useProgrammes();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProgram, setEditProgram] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchDepartments();
    fetchLecturers();
    fetchProgramCategories();
    fetchProgrammes();
  }, []);

  const ITEMS_PER_PAGE = 5;
  const filteredPrograms = programmes.filter((program) =>
    program.title.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filteredPrograms.length / ITEMS_PER_PAGE);
  const start = (page - 1) * ITEMS_PER_PAGE;
  const currentPrograms = filteredPrograms.slice(start, start + ITEMS_PER_PAGE);

  const toggleStatus = (program) => {
    const formData = new FormData();
    formData.append("status", program.status === "Active" ? "Inactive" : "Active");
    updateProgramme(program.id, formData);
  };

  const handleAddProgram = (formData) => {
    addProgramme(formData);
    setShowAddModal(false);
  };

  const handleUpdateProgram = (formData) => {
    if (!editProgram) return;
    updateProgramme(editProgram.id, formData);
    setShowEditModal(false);
    setEditProgram(null);
  };

  const handleDeleteProgram = (id) => {
    if (window.confirm("Are you sure you want to delete this program?")) {
      deleteProgramme(id);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb]">
      <div className="p-6 sm:p-8">
        {/* HERO SECTION */}
        <div className="rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden mb-8">
          <div className="relative p-7 sm:p-10">
            <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-violet-200/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-amber-200/25 blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="violet">
                    <GraduationCap className="h-4 w-4 mr-1" />
                    Program Management
                  </Pill>
                  <Pill tone="gray">Admin Portal</Pill>
                  <Pill tone="green">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Career Programs
                  </Pill>
                </div>

                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900">
                  Programs
                </h1>
                <p className="mt-3 text-gray-600 text-sm sm:text-base">
                  Manage comprehensive career programs with placement opportunities, salary packages, and structured learning paths.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-gray-600">Total</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {filteredPrograms.length} programs
                  </div>
                </div>

                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-extrabold text-white hover:bg-red-700 transition shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  Add Program
                </button>
              </div>
            </div>
          </div>
        </div>

        <SectionDivider
          label="Browse Programs"
          right={
            <div className="flex items-center gap-2">
              {search && <Pill tone="amber" size="sm">Search: {search}</Pill>}
              <Pill tone="blue" size="sm">Page {page} of {totalPages}</Pill>
            </div>
          }
        />

        {/* SEARCH */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search programs..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Program Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Organization
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Program Info
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Placement & Salary
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {currentPrograms.map((program, index) => (
                  <tr
                    key={program.id}
                    className={cx(
                      "hover:bg-gray-50 transition-colors",
                      index !== currentPrograms.length - 1 ? "border-b border-gray-100" : ""
                    )}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-2xl bg-violet-100 flex items-center justify-center">
                          <GraduationCap className="h-6 w-6 text-violet-600" />
                        </div>
                        <div>
                          <div className="font-extrabold text-gray-900">{program.title}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <Pill tone="violet" size="sm">{program.type}</Pill>
                            <Pill
                              tone={program.mode === "Online" ? "blue" : program.mode === "Offline" ? "green" : "purple"}
                              size="sm"
                            >
                              <MapPin className="h-3 w-3 mr-1" />
                              {program.mode}
                            </Pill>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-900">{program.category_name}</span>
                        </div>
                        <div className="text-xs text-gray-600">{program.programe_category_name}</div>
                        <div className="text-xs text-gray-600">{program.department_name}</div>
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-green-600" />
                          <span className="text-xs text-gray-600">{program.lecturer_name}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-amber-600" />
                          <span className="text-sm font-medium text-gray-900">{program.duration}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-semibold text-green-700">₹{program.salary}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Award className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-gray-700">{program.placed}% placed</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleStatus(program)}
                        className="transition-all duration-200"
                      >
                        <Pill
                          tone={program.status === "Active" ? "green" : "yellow"}
                          size="sm"
                        >
                          {program.status}
                        </Pill>
                      </button>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setEditProgram(program); setShowEditModal(true); }}
                          className="p-2 rounded-2xl hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                          aria-label="Edit program"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProgram(program.id)}
                          className="p-2 rounded-2xl hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
                          aria-label="Delete program"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {currentPrograms.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                          <GraduationCap className="h-6 w-6 text-gray-400" />
                        </div>
                        <div>
                          <div className="font-extrabold text-gray-900">No programs found</div>
                          <p className="mt-1 text-sm text-gray-600">
                            {search ? "Try adjusting your search terms." : "Get started by adding your first program."}
                          </p>
                        </div>
                        {!search && (
                          <button
                            onClick={() => setShowAddModal(true)}
                            className="mt-2 inline-flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-sm font-extrabold text-white hover:bg-red-700 transition"
                          >
                            <Plus className="h-4 w-4" />
                            Add Program
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8">
            <button
              onClick={() => setPage(Math.max(page - 1, 1))}
              disabled={page === 1}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === 1
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={cx(
                    "px-4 py-2 rounded-2xl text-sm font-extrabold transition-colors",
                    pageNum === page
                      ? "bg-red-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-gray-200"
                  )}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => setPage(Math.min(page + 1, totalPages))}
              disabled={page === totalPages}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === totalPages
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* MODALS */}
        {showAddModal && (
          <ProgramModal
            title="Add Program"
            onClose={() => setShowAddModal(false)}
            onSave={handleAddProgram}
          />
        )}
        {showEditModal && editProgram && (
          <ProgramModal
            title="Edit Program"
            program={editProgram}
            onClose={() => { setShowEditModal(false); setEditProgram(null); }}
            onSave={handleUpdateProgram}
          />
        )}
      </div>
    </div>
  );
}

/* ================= MODAL ================= */

function ProgramModal({ title, onClose, onSave, program }) {
  const { categories } = useCategories();
  const { departments } = useDepartments();
  const { lecturers } = useLecturers();
  const { programCategories } = useProgramCategories();

  const [form, setForm] = useState(
    program || {
      category_id: "",
      department_id: "",
      program_lecturer_id: "",
      program_category_id: "",
      title: "",
      salary: "",
      duration: "",
      mode: "Online",
      type: "",
      placed: "",
      syllabus: "",
      image: "",
      status: "Active",
    }
  );

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(
    program?.image ? `${IMG_URL}/programs/${program.image}` : null
  );

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const normalizeSyllabusForUI = (syllabus) => {
    if (!syllabus) return "";

    try {
      if (typeof syllabus === "string" && syllabus.startsWith("[")) {
        const parsed = JSON.parse(syllabus);

        if (Array.isArray(parsed) && parsed.length === 1 && parsed[0].includes(",")) {
          return parsed[0]
            .split(",")
            .map(s => s.trim())
            .join(", ");
        }

        if (Array.isArray(parsed)) {
          return parsed.join(", ");
        }
      }

      return syllabus;
    } catch (e) {
      console.error("Syllabus parse error:", e);
      return syllabus;
    }
  };

  useEffect(() => {
    if (program) {
      setForm({
        ...program,
        syllabus: normalizeSyllabusForUI(program.syllabus),
      });
    }
  }, [program]);

  const handleSave = () => {
    if (!form.title.trim()) { alert("Title is required"); return; }
    if (!form.status) { alert("Status is required"); return; }

    const formData = new FormData();
    formData.append("title", form.title.trim());
    formData.append("status", form.status);
    formData.append("category_id", form.category_id || "");
    formData.append("department_id", form.department_id || "");
    formData.append("program_lecturer_id", form.program_lecturer_id || "");
    formData.append("program_category_id", form.program_category_id || "");
    formData.append("salary", form.salary || "");
    formData.append("duration", form.duration || "");
    formData.append("mode", form.mode || "");
    formData.append("type", form.type || "");
    formData.append("placed", form.placed || "");
    formData.append("create_audit_id", JSON.parse(localStorage.getItem("user") || "{}").id || "");

    if (form.syllabus) {
      const syllabusArr = Array.isArray(form.syllabus)
        ? form.syllabus
        : form.syllabus.split("\n").map((s) => s.trim()).filter(Boolean);
      formData.append("syllabus", JSON.stringify(syllabusArr));
    }

    if (imageFile) {
      formData.append("image", imageFile);
    }

    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-4xl bg-white rounded-3xl ring-1 ring-gray-200 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-extrabold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-2xl hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Program Title
              </label>
              <input
                type="text"
                placeholder="Program Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Select Category
              </label>
              <select
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Select Department
              </label>
              <select
                value={form.department_id}
                onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="">Select Department</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Select Lecturer
              </label>
              <select
                value={form.program_lecturer_id}
                onChange={(e) => setForm({ ...form, program_lecturer_id: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="">Select Lecturer</option>
                {lecturers.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Select Program Category
              </label>
              <select
                value={form.program_category_id}
                onChange={(e) => setForm({ ...form, program_category_id: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="">Select Program Category</option>
                {programCategories.map((pc) => (
                  <option key={pc.id} value={pc.id}>{pc.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Salary
              </label>
              <input
                type="text"
                placeholder="Salary"
                value={form.salary}
                onChange={(e) => setForm({ ...form, salary: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Duration
              </label>
              <input
                type="text"
                placeholder="Duration"
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Mode
              </label>
              <select
                value={form.mode}
                onChange={(e) => setForm({ ...form, mode: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="Online">Online</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Offline">Offline</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Program Type
              </label>
              <input
                type="text"
                placeholder="Program Type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Placement Rate (%)
              </label>
              <input
                type="text"
                placeholder="Placement Rate"
                value={form.placed}
                onChange={(e) => setForm({ ...form, placed: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Syllabus
              </label>
              <input
                type="text"
                placeholder="Syllabus (comma separated)"
                value={form.syllabus}
                onChange={(e) => setForm({ ...form, syllabus: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-extrabold text-gray-900 mb-2">
                Program Image
              </label>
              <div className="space-y-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-2xl file:border-0 file:text-sm file:font-extrabold file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
                />

                {preview && (
                  <div className="flex items-center gap-3">
                    <img
                      src={preview}
                      alt="Program Preview"
                      className="h-24 w-24 object-cover rounded-2xl ring-1 ring-gray-200"
                    />
                    <div className="text-sm text-gray-600">
                      Program image preview
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            className="w-full mt-6 bg-red-600 text-white py-3 rounded-2xl font-extrabold hover:bg-red-700 transition-colors shadow-sm"
          >
            Save Program
          </button>
        </div>
      </div>
    </div>
  );
}
